/**
 * 助力功能工具函数模块
 * 提供gid获取、接口Mock等功能
 */

import crypto from '@/utils/crypto';
import getDeviceInfo from '@ky/sdk/dist/getDeviceInfo';
import { get, post } from './utils';

/**
 * 获取快手gid
 * 使用项目已有的 bridge 工具避免多实例问题
 * @returns {Promise<string>} 返回gid
 */
export function getKuaishouGid() {
  return getDeviceInfo()
    .then((deviceInfo) => {
      if (deviceInfo && deviceInfo.globalId) {
        console.log('[Assist] 获取gid成功:', deviceInfo.globalId);
        return deviceInfo.globalId;
      }
      throw new Error('gid not found');
    })
    .catch((error) => {
      console.warn('[Assist] 从快手端获取gid失败:', error);
      return '';
    });
}

/**
 * 获取加密参数
 * 使用crypto函数加密，参考 src/utils/get.js
 * @returns {Object} 加密参数对象 {timestamp, nonce, sign}
 */
export function getEncryptParams() {
  return crypto('yiuhjkbvhbjisjchgdnx38uejd');
}

/**
 * Mock助力状态查询
 * 后端根据 shareId 和 gid 判断用户身份，返回 role 字段
 * 前端通过URL参数 mockRole 和 mockAssisted 控制Mock返回数据
 * 
 * @param {string} shareId - 助力唯一ID
 * @param {string} gid - 快手端gid
 * @returns {Promise<Object>} 返回助力状态信息
 */
export async function mockCheckAssistStatus(shareId, gid) {
  const urlParams = new URLSearchParams(window.location.search);
  // 支持 role 和 mockRole 两个参数
  const mockRole = urlParams.get('role') || urlParams.get('mockRole') || 'receiver'; // 默认接收方
  // 支持 assisted 和 mockAssisted 两个参数
  const mockAssisted = urlParams.get('assisted') === 'true' || urlParams.get('mockAssisted') === 'true';
  
  // 获取模板ID和from参数，构造跳转链接
  const templateId = urlParams.get('id');
  const from = urlParams.get('from') || 'mv_share_h5';
  const baseJumpUrl = templateId 
    ? `kwaiying://mv?id=${templateId}&from=${from}` 
    : 'kwaiying://mv';
  
  console.log('[Assist Mock] 查询助力状态:', { shareId, gid, mockRole, mockAssisted, baseJumpUrl });
  
  // 模拟网络延迟
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const mockData = {
    sender_not_assisted: {
      role: 'sender',
      jumpUrl: baseJumpUrl,
      buttonText: '等好友助力',
      viewType: 'sender_waiting'
    },
    sender_assisted: {
      role: 'sender',
      jumpUrl: 'kwaiying://krn?bundleId=half_screen&componentName=template_detail',
      buttonText: '已助力，去【快影】使用',
      viewType: 'sender_success'
    },
    receiver_not_assisted: {
      role: 'receiver',
      jumpUrl: '',  // 不跳转，执行助力
      buttonText: '立即助力',
      viewType: 'receiver_can_assist'
    },
    receiver_assisted: {
      role: 'receiver',
      jumpUrl: baseJumpUrl,
      buttonText: '去邀请好友助力',
      viewType: 'receiver_already_assisted'
    }
  };
  
  const key = `${mockRole}_${mockAssisted ? 'assisted' : 'not_assisted'}`;
  const result = {
    result: 1,
    data: mockData[key] || mockData.receiver_not_assisted
  };
  
  console.log('[Assist Mock] 查询结果:', result);
  return result;
}

/**
 * Mock执行助力
 * 
 * @param {string} shareId - 助力唯一ID
 * @param {string} gid - 快手端gid
 * @returns {Promise<Object>} 返回助力执行结果
 */
export async function mockExecuteAssist(shareId, gid) {
  console.log('[Assist Mock] 执行助力:', { shareId, gid });
  
  // 模拟网络延迟
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // 可以通过URL参数 mockAssistFail=true 模拟助力失败
  const urlParams = new URLSearchParams(window.location.search);
  const shouldFail = urlParams.get('mockAssistFail') === 'true';
  
  const result = {
    result: 1,
    data: {
      assistStatus: shouldFail ? 'FAILED' : 'SUCCESS'
    }
  };
  
  console.log('[Assist Mock] 助力结果:', result);
  return result;
}

/**
 * 真实接口调用
 */

/**
 * 查询助力状态
 * @param {string} shareId - 助力唯一ID
 * @param {string} gid - 快手端gid
 * @returns {Promise<Object>} 返回助力状态信息
 */
export async function checkAssistStatus(shareId, gid) {
  const { data } = await get('/rest/n/kmovie/social/share/assist/check', {
    shareId,
    gid,
    ...getEncryptParams()
  });
  return data;
}

/**
 * 执行助力
 * @param {string} shareId - 助力唯一ID
 * @param {string} gid - 快手端gid
 * @returns {Promise<Object>} 返回助力执行结果
 */
export async function executeAssist(shareId, gid) {
  const { data } = await post('/rest/n/kmovie/social/share/assist', {
    shareId,
    gid,
    ...getEncryptParams()
  });
  return data;
}
